import { Link } from "react-router-dom";
import Seo from "../Components/Seo.jsx";
import PageHeader from "../Components/PageHeader.jsx";
import { VenueCard, EnquiryCta } from "../Components/SiteInfo.jsx";
import { ORG } from "../seo/site.js";
import { TAMIL_PAGES_REVIEWED, TAMIL_CLASSES_PAGE } from "../seo/pages.js";
import classPhoto from "../assets/Images/1.jpeg";

const PATH = "/tamil-classes-sydney/";

const learningAreas = [
  { icon: "bi-ear", title: "Listening and speaking", text: "Everyday conversation, songs and stories build confidence to understand and speak Tamil at home and in the community." },
  { icon: "bi-book", title: "Reading", text: "Children learn the Tamil script step by step, from letters and sounds to reading texts." },
  { icon: "bi-pencil", title: "Writing", text: "Writing grows from letter formation to sentences, paragraphs and longer pieces as children move through the stages." },
  { icon: "bi-stars", title: "Culture, values and history", text: "Tamil festivals, traditions, values and history are part of learning at every stage." },
];

// Answers use confirmed facts only; times, fees and active levels are still
// awaiting WSTSC confirmation (OWNER_INPUT_NEEDED.md), so those point to us.
// Deliberately no FAQPage schema: these are for parents, not rich results.
const faqs = [
  {
    q: "Where are the Tamil classes held?",
    a: (
      <>
        At {ORG.venue.name}, 158 Walters Road, Blacktown NSW 2148, which hosts
        WSTSC. <Link to="/contact/" className="red-text fw-semibold">See the map and directions</Link>.
      </>
    ),
  },
  {
    q: "Who can join?",
    a: "Children who want to learn or strengthen their Tamil. Our curriculum runs from Preschool to Year 12 and we welcome students of all backgrounds and abilities. Please contact us to confirm which levels have classes this year.",
  },
  {
    q: "When do classes run?",
    a: "Classes run outside mainstream school hours. Class times and term dates are confirmed each year, so please contact us for the current timetable.",
  },
  {
    q: "What will my child learn?",
    a: "Listening, speaking, reading and writing in Tamil, together with Tamil history, values and cultural traditions such as festivals and performances.",
  },
  {
    q: "How do I enrol my child?",
    a: (
      <>
        Complete the online{" "}
        <Link to="/eoi/" className="red-text fw-semibold">enrolment expression of interest</Link>.
        You will verify your mobile number, add your children's details, and our team will email you the next steps.
      </>
    ),
  },
  {
    q: "How much are the fees?",
    a: `Fees are confirmed each year. Please call ${ORG.phones.committee.display} or email ${ORG.email} for the current fees.`,
  },
  {
    q: "Is WSTSC a not-for-profit school?",
    a: "Yes. WSTSC is a not-for-profit community language school, run with the help of parents, volunteers and community leaders.",
  },
];

const TamilClassesSydney = () => (
  <>
    <Seo path={PATH} />
    <PageHeader
      title="Tamil Classes in Sydney"
      breadcrumbs={[
        { label: "Home", href: "/", icon: "bi-house" },
        { label: "Tamil Classes in Sydney", active: true },
      ]}
    />

    <section className="py-5">
      <div className="container py-lg-4">
        <div className="row g-4 g-lg-5 align-items-center">
          <div className="col-lg-7">
            <p className="text-uppercase fw-bold red-text mb-2">
              Children's Tamil language classes
            </p>
            {TAMIL_PAGES_REVIEWED && (
              <p className="mb-3">
                <Link to={TAMIL_CLASSES_PAGE.path} lang="ta" hrefLang="ta" className="blue-text fw-semibold tamil-font">
                  <i className="bi bi-translate me-1" aria-hidden="true"></i>
                  இப்பக்கத்தைத் தமிழில் படிக்க
                </Link>
              </p>
            )}
            <p className="fs-5 mb-3">
              {ORG.name} (WSTSC) teaches Tamil language and culture to children
              in Western Sydney, at {ORG.venue.name}, 158 Walters Road,
              Blacktown.
            </p>
            <p className="text-muted mb-3">
              We are a not-for-profit community language school. Classes run
              outside mainstream school hours and are taught by experienced
              Tamil language educators. Parents, volunteers and community
              leaders work together to keep the school running.
            </p>
            <p className="text-muted mb-0">
              Our curriculum covers listening, speaking, reading and writing,
              alongside Tamil history, values and cultural traditions. It
              follows a pathway from Preschool to Year 12.
            </p>
          </div>
          <div className="col-lg-5">
            <img
              src={classPhoto}
              alt="WSTSC students in traditional dress seated with their families at a school gathering"
              className="img-fluid rounded-5 border-bottom-left shadow-sm"
              width="2048"
              height="1366"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>

    <section className="py-5" style={{ background: "#f9f9f9" }}>
      <div className="container">
        <h2 className="fw-bold mb-3">Who the classes are for</h2>
        <div className="row g-4">
          <div className="col-md-6">
            <p className="text-muted">
              Our classes are for children who want to learn or strengthen
              their Tamil. That includes children from Tamil-speaking families
              who are growing up with English at school. We welcome students of
              all backgrounds and abilities.
            </p>
          </div>
          <div className="col-md-6">
            <p className="text-muted">
              Learning is organised in stages from Preschool to Year 12. Please{" "}
              <Link to="/contact/" className="red-text fw-semibold">contact us</Link>{" "}
              to confirm which levels have classes this year and where your
              child would fit. You can see what each stage covers on our{" "}
              <Link to="/programs/" className="red-text fw-semibold">
                programs and learning levels
              </Link>{" "}
              page.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="py-5">
      <div className="container">
        <h2 className="fw-bold mb-4">What your child will learn</h2>
        <div className="row g-4">
          {learningAreas.map((area) => (
            <div className="col-md-6 col-lg-3" key={area.title}>
              <div className="h-100 border rounded-4 border-bottom-left p-4">
                <div className="d-flex align-items-center gap-3 mb-2">
                  <i className={`bi ${area.icon} fs-2 blue-text flex-shrink-0`} aria-hidden="true"></i>
                  <h3 className="fs-5 fw-bold mb-0">{area.title}</h3>
                </div>
                <p className="text-muted mb-0">{area.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-muted mt-4 mb-0">
          Lessons are student-centred, interactive and matched to each age
          group.{" "}
          <Link to="/about/" className="red-text fw-semibold">
            Read about our teaching approach
          </Link>
          .
        </p>
      </div>
    </section>

    <section className="py-5" style={{ background: "#f9f9f9" }}>
      <div className="container">
        <div className="row g-4 align-items-stretch">
          <div className="col-lg-6">
            <h2 className="fw-bold mb-3">Where and when</h2>
            <p className="text-muted">
              Classes are held at {ORG.venue.name}, which hosts WSTSC and lists
              the centre on its{" "}
              <a
                href={ORG.hostSchoolPage}
                target="_blank"
                rel="noopener noreferrer"
                className="red-text fw-semibold"
              >
                school website
                <span className="visually-hidden"> (opens in a new tab)</span>
              </a>
              .
            </p>
            <p className="text-muted">
              The school is in Blacktown, in Western Sydney, a short drive from
              nearby suburbs such as Seven Hills, Doonside, Quakers Hill and
              Mount Druitt. Ask us about arrival and parking before your first
              visit.
            </p>
            <p className="text-muted mb-0">
              Class times, term dates and fees are confirmed each year. Contact
              us for the current timetable and fees, or submit an expression of
              interest and we will send you the details.
            </p>
          </div>
          <div className="col-lg-6">
            <VenueCard className="h-100" />
          </div>
        </div>
      </div>
    </section>

    <section className="py-5">
      <div className="container">
        <h2 className="fw-bold mb-4">How to enrol</h2>
        <ol className="ps-3 mb-4">
          <li className="mb-2">
            Fill in the online{" "}
            <Link to="/eoi/" className="red-text fw-semibold">
              enrolment expression of interest
            </Link>{" "}
            with your details and your children's details.
          </li>
          <li className="mb-2">
            Our team reviews your application and emails you the next steps.
          </li>
          <li>If you have questions at any point, call or email us.</li>
        </ol>
        <Link to="/enrolment/" className="blue-text fw-semibold text-decoration-none">
          More about the enrolment process
          <i className="bi bi-arrow-right ms-1" aria-hidden="true"></i>
        </Link>
      </div>
    </section>

    <section className="py-5" style={{ background: "#f9f9f9" }}>
      <div className="container">
        <h2 className="fw-bold mb-4">Questions parents ask</h2>
        <div className="faq-list col-lg-9">
          {faqs.map((faq) => (
            <details key={faq.q} className="faq-item bg-white border rounded-4 mb-3">
              <summary className="fw-semibold p-3 px-md-4">{faq.q}</summary>
              <div className="text-muted px-3 px-md-4 pb-3">{faq.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>

    <EnquiryCta />
  </>
);

export default TamilClassesSydney;
